import { useState, useEffect, useMemo, useRef } from 'react'
import { motion } from 'motion/react'
import { X, Check } from 'lucide-react'
import { InputType, PopupCloseSetting } from '@/constants'
import { useSessionStore } from '@/stores'
import { apiClient } from '@/services/api'
import StepTitle from '@/components/StepTitle'
import StepMedia from './StepMedia'
import SessionContent from './SessionContent'
import OrderItems from './OrderItems'

const StatusDisplayState = Object.freeze({
  status: 0,
  itemDetails: 1,
  destroyed: -1,
})

interface InputActionStatusProps {
  input?: any
  /** Explicit session to render against — pass popupSession in popup context. */
  session?: any
  onDone?: (input?: any) => void
  onBack?: () => void
  onEdit?: (input: any, item: any) => void
  /** The host already provides a close affordance (OMP drawer header). */
  hideClose?: boolean
}

const InputActionStatus = ({ input, session: sessionProp, onDone, onBack, hideClose }: InputActionStatusProps) => {
  const storeSession = useSessionStore(state => state.session) as any
  const popupSession = useSessionStore(state => state.popupSession) as any
  const session = sessionProp ?? (storeSession?.is_popup && popupSession ? popupSession : storeSession)
  const isLastItemFn = useSessionStore(state => state.isLastItem)
  const canShowCloseIconFn = useSessionStore(state => state.canShowCloseIcon)
  const popupCloseCanUndoFn = useSessionStore(state => state.popupCloseCanUndo)
  const popupCloseCanSelectFirstInputFn = useSessionStore(state => state.popupCloseCanSelectFirstInput)
  const popupCloseCanSelectLastInputFn = useSessionStore(state => state.popupCloseCanSelectLastInput)
  const storedDisplayState = useSessionStore(state => state.popupDisplayState)
  const setStoredDisplayState = useSessionStore(state => state.setPopupDisplayState)

  const [displayState, setDisplayState] = useState<number>(
    storedDisplayState === StatusDisplayState.destroyed || storedDisplayState == null
      ? StatusDisplayState.status
      : storedDisplayState
  )
  const [returnLabelText, setReturnLabelText] = useState<string | null>(null)
  const [returnQRCodeText, setReturnQRCodeText] = useState<string | null>(null)

  const isLegacy = useMemo(() => !!session?.is_legacy, [session])
  const isLastItem = isLastItemFn?.() ?? false

  const selectedItem = session?.selected_item
  const newItem = session?.new_item
  const detailItems = useMemo(() => {
    const list: any[] = []
    if (selectedItem) {
      list.push(selectedItem)
    }
    if (newItem) {
      list.push(newItem)
    }
    return list
  }, [selectedItem, newItem])
  const showPopupBackButton = (() => {
    if (!session?.is_popup || session?.is_background_session) {
      return false
    }
    if (!session.can_undo && !session.is_step_preview) {
      return false
    }
    const undoSettings = [PopupCloseSetting.Undo, PopupCloseSetting.UndoOrFirstInput, PopupCloseSetting.UndoOrLastInput]
    return !session.popup_close_setting || !undoSettings.includes(session.popup_close_setting)
  })()
  const canShowCloseIcon = canShowCloseIconFn?.() ?? false
  const popupCloseCanUndo = popupCloseCanUndoFn?.() ?? false
  const popupCloseCanSelectFirstInput = popupCloseCanSelectFirstInputFn?.() ?? false
  const popupCloseCanSelectLastInput = popupCloseCanSelectLastInputFn?.() ?? false

  useEffect(() => {
    setStoredDisplayState(displayState)
  }, [displayState, setStoredDisplayState])

  useEffect(() => {
    return () => {
      setStoredDisplayState(StatusDisplayState.destroyed)
    }
  }, [setStoredDisplayState])

  // Show our success badge only when the backend supplies no media of its own.
  const stepStaticData = (session as any)?.step_static_data
  const hasBackendMedia = !!(
    stepStaticData?.image ||
    stepStaticData?.image_mobile ||
    stepStaticData?.image_tablet ||
    stepStaticData?.embed_code
  )

  const buttonClass = !isLegacy ? 'btn-base input-choice-btn' : 'btn-base btn-primary btn-account'
  const backClass = !isLegacy ? 'btn-base back-btn' : 'btn-base btn-outline-success'
  // .btn-account / .input-choice-btn / .back-btn all hardcode `width: 100%` (unlayered,
  // beats any Tailwind utility), so we cap the on-screen width via inline `maxWidth`
  // instead. Two of these fit comfortably side-by-side on every modal width above sm.
  const popupBtnStyle: React.CSSProperties = { maxWidth: '220px' }

  // Tracks backend-rendered path-input buttons for return label / QR code so we
  // can hide them (this component renders inline equivalents) and restore them
  // on unmount. Restoring matters because navigating between steps reuses the
  // surrounding DOM and we don't want stale `display:none` on it.
  const hiddenButtonsRef = useRef<HTMLElement[]>([])

  useEffect(() => {
    const additionalInputs = session?.additional_inputs as any[] | undefined
    if (!additionalInputs?.length) {
      return
    }

    const typesToHide: number[] = []
    additionalInputs.forEach((additionalInput: any) => {
      if (additionalInput.type === InputType.ShipmentReturnLabel) {
        setReturnLabelText(additionalInput.formatted_label)
        typesToHide.push(additionalInput.type)
      } else if (additionalInput.type === InputType.ShipmentReturnQRCode) {
        setReturnQRCodeText(additionalInput.formatted_label)
        typesToHide.push(additionalInput.type)
      }
    })

    if (!typesToHide.length) {
      return
    }

    // Run after the path-input retry loop has had a chance to render content.
    const timer = window.setTimeout(() => {
      typesToHide.forEach(type => {
        const el = document.querySelector(`[inputtype='${type}']`) as HTMLElement | null
        if (el && el.style.display !== 'none') {
          hiddenButtonsRef.current.push(el)
          el.style.display = 'none'
        }
      })
    }, 100)

    return () => {
      window.clearTimeout(timer)
      hiddenButtonsRef.current.forEach(el => {
        el.style.display = ''
      })
      hiddenButtonsRef.current = []
    }
  }, [session?.additional_inputs])

  const getShipmentReturnLabel = async (qrCode: boolean) => {
    const item = (session as any)?.selected_item
    if (!item) {
      return
    }
    const newWindow = window.open('', '_blank')
    try {
      const response = await apiClient.get(`/order_items/${item.id}/return_label/`, {
        params: { qr_code: qrCode ? 'true' : '' },
      })
      const labelUrl = response.data?.label_url || response.data?.url
      if (labelUrl && newWindow) {
        newWindow.location.href = labelUrl
      } else if (newWindow) {
        newWindow.close()
      }
    } catch {
      if (newWindow) {
        newWindow.close()
      }
    }
  }

  const goBack = () => {
    if (displayState === StatusDisplayState.status) {
      onBack?.()
    } else {
      setDisplayState(StatusDisplayState.status)
    }
  }

  const closePopup = () => {
    if (popupCloseCanUndo) {
      onBack?.()
    } else if (popupCloseCanSelectFirstInput) {
      const first = session?.valid_inputs?.[0]
      if (first) {
        ;(onDone as any)?.(first)
      } else {
        onDone?.()
      }
    } else if (popupCloseCanSelectLastInput) {
      const arr = session?.valid_inputs
      const last = arr?.[arr.length - 1]
      if (last) {
        ;(onDone as any)?.(last)
      } else {
        onDone?.()
      }
    }
  }

  const showButtons = showPopupBackButton || isLastItem || !session?.enhanced_mode

  if (displayState === StatusDisplayState.itemDetails) {
    return (
      <div className="input-action-status">
        <OrderItems
          key={`action-status-${input?.id}`}
          input={input}
          items={detailItems}
          onSubmit={() => onDone?.()}
          onBack={goBack}>
          <StepTitle hr>
            <div>Select Items for Status</div>
          </StepTitle>
        </OrderItems>
        {canShowCloseIcon && !hideClose && !session?.is_popup && (
          <button type="button" className="close-button flex justify-center items-center" onClick={closePopup}>
            <X size={20} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="input-action-status">
      <StepMedia session={session as any} />

      {!hasBackendMedia && (
        <motion.div
          className="flex justify-center mb-5"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.05 }}>
          <div className="input-action-status-success-badge">
            <Check size={36} strokeWidth={3} />
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.2, ease: [0.16, 1, 0.3, 1] as const }}>
        <SessionContent currentSession={session} />
      </motion.div>

      {showButtons && (
        <div className="flex flex-wrap justify-center items-stretch gap-3 mt-6 mb-3 w-full">
          {isLastItem && (
            <button
              type="button"
              className={buttonClass}
              style={popupBtnStyle}
              onClick={() => {
                // Push to the store synchronously so SessionContainer's modal-size
                // selector picks up the change in the SAME render as itemDetails.
                // Without this, useEffect syncs a frame later and the skeleton lays
                // out into the narrow `lg` modal before the `xl` size lands.
                setStoredDisplayState(StatusDisplayState.itemDetails)
                setDisplayState(StatusDisplayState.itemDetails)
              }}>
              View Details
            </button>
          )}

          {(isLastItem || !session?.enhanced_mode) && (
            <button type="button" className={buttonClass} style={popupBtnStyle} onClick={() => onDone?.()}>
              <span dangerouslySetInnerHTML={{ __html: input?.formatted_label || '' }} />
            </button>
          )}

          {returnLabelText && (
            <button
              type="button"
              className={buttonClass}
              style={popupBtnStyle}
              dangerouslySetInnerHTML={{ __html: returnLabelText }}
              onClick={() => getShipmentReturnLabel(false)}
            />
          )}

          {returnQRCodeText && (
            <button
              type="button"
              className={buttonClass}
              style={popupBtnStyle}
              dangerouslySetInnerHTML={{ __html: returnQRCodeText }}
              onClick={() => getShipmentReturnLabel(true)}
            />
          )}

          {showPopupBackButton && (
            <button type="button" className={backClass} style={popupBtnStyle} onClick={goBack}>
              Back
            </button>
          )}
        </div>
      )}

      {canShowCloseIcon && !hideClose && !session?.is_popup && (
        <button type="button" className="close-button flex justify-center items-center" onClick={closePopup}>
          <X size={20} />
        </button>
      )}
    </div>
  )
}

export default InputActionStatus
